import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import workflowTemplateService from '../../../services/workflowTemplateService';
import templateCategoryService from '../../../services/templateCategoryService';

const TemplateManagementPanel = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [viewMode, setViewMode] = useState('grid');
  const [templates, setTemplates] = useState([]);
  const [categories, setCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Category management state
  const [showCategoryDialog, setShowCategoryDialog] = useState(false);
  const [categoryName, setCategoryName] = useState('');
  const [categoryValue, setCategoryValue] = useState('');
  const [editingCategory, setEditingCategory] = useState(null);
  const [isSavingCategory, setIsSavingCategory] = useState(false);

  // Template management state
  const [showTemplateDialog, setShowTemplateDialog] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [templateDescription, setTemplateDescription] = useState('');
  const [templateCategory, setTemplateCategory] = useState('custom');
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [isSavingTemplate, setIsSavingTemplate] = useState(false);
  const [openMenuId, setOpenMenuId] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    // Auto-delete specific templates on mount
    const deleteSpecificTemplates = async () => {
      try {
        const templatesToDelete = ['Quality Check Flow', 'Assembly Line Setup'];
        await workflowTemplateService?.deleteTemplatesByName(templatesToDelete);
        console.log('Successfully deleted specified templates');
      } catch (err) {
        console.error('Error deleting specific templates:', err);
      }
    };
    
    deleteSpecificTemplates();
  }, []);

  const loadData = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const [templatesData, categoriesData] = await Promise.all([
        workflowTemplateService?.getAllTemplates(),
        templateCategoryService?.getAllCategories()
      ]);
      setTemplates(templatesData);
      setCategories(categoriesData);
    } catch (err) {
      console.error('Error loading data:', err);
      
      // Check if it's a table not found error
      if (err?.code === 'PGRST205' || err?.message?.includes('Could not find the table')) {
        setError('Database table "node_categories" not found. Please push your Supabase migrations to create the required tables.');
      } else {
        setError(err?.message || 'Failed to load data');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateCategory = () => {
    setEditingCategory(null);
    setCategoryName('');
    setCategoryValue('');
    setShowCategoryDialog(true);
  };

  const handleEditCategory = (category) => {
    setEditingCategory(category);
    setCategoryName(category?.categoryLabel);
    setCategoryValue(category?.categoryValue);
    setShowCategoryDialog(true);
  };

  const handleSaveCategory = async () => {
    if (!categoryName?.trim() || !categoryValue?.trim()) {
      alert('Please fill in all fields');
      return;
    }

    try {
      setIsSavingCategory(true);
      if (editingCategory) {
        await templateCategoryService?.updateCategory(editingCategory?.id, {
          categoryLabel: categoryName,
          categoryValue: categoryValue
        });
      } else {
        await templateCategoryService?.createCategory({
          categoryLabel: categoryName,
          categoryValue: categoryValue
        });
      }
      await loadData();
      setShowCategoryDialog(false);
      setCategoryName('');
      setCategoryValue('');
      setEditingCategory(null);
    } catch (err) {
      console.error('Error saving category:', err);
      alert(err?.message || 'Failed to save category');
    } finally {
      setIsSavingCategory(false);
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!confirm('Are you sure you want to delete this category?')) return;

    try {
      await templateCategoryService?.deleteCategory(categoryId);
      await loadData();
    } catch (err) {
      console.error('Error deleting category:', err);
      alert(err?.message || 'Failed to delete category');
    }
  };

  const handleCreateTemplate = () => {
    setEditingTemplate(null);
    setTemplateName('');
    setTemplateDescription('');
    setTemplateCategory('custom');
    setShowTemplateDialog(true);
  };

  const handleEditTemplate = (template) => {
    setEditingTemplate(template);
    setTemplateName(template?.name);
    setTemplateDescription(template?.description || '');
    setTemplateCategory(template?.category || 'custom');
    setShowTemplateDialog(true);
  };

  const handleSaveTemplate = async () => {
    if (!templateName?.trim()) {
      alert('Please enter a template name');
      return;
    }

    try {
      setIsSavingTemplate(true);
      if (editingTemplate) {
        await workflowTemplateService?.updateTemplate(editingTemplate?.id, {
          name: templateName,
          description: templateDescription,
          category: templateCategory,
          nodes: editingTemplate?.nodes || [],
          connections: editingTemplate?.connections || [],
          tags: editingTemplate?.tags || []
        });
      } else {
        await workflowTemplateService?.createTemplate({
          name: templateName,
          description: templateDescription,
          category: templateCategory,
          nodes: [],
          connections: [],
          tags: []
        });
      }
      await loadData();
      setShowTemplateDialog(false);
      setTemplateName('');
      setTemplateDescription('');
      setTemplateCategory('custom');
      setEditingTemplate(null);
    } catch (err) {
      console.error('Error saving template:', err);
      alert(err?.message || 'Failed to save template');
    } finally {
      setIsSavingTemplate(false);
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!confirm('Are you sure you want to delete this template?')) return;

    try {
      await workflowTemplateService?.deleteTemplate(templateId);
      await loadData();
    } catch (err) {
      console.error('Error deleting template:', err);
      alert(err?.message || 'Failed to delete template. System templates cannot be deleted.');
    }
  };

  const handleCloneTemplate = async (template) => {
    try {
      await workflowTemplateService?.createTemplate({
        name: `${template?.name} (Copy)`,
        description: template?.description,
        category: template?.category,
        nodes: template?.nodes || [],
        connections: template?.connections || [],
        tags: template?.tags || []
      });
      await loadData();
    } catch (err) {
      console.error('Error cloning template:', err);
      alert(err?.message || 'Failed to clone template');
    }
  };

  const categoryOptions = [
    { value: 'all', label: 'All Categories' },
    ...categories?.map(cat => ({
      value: cat?.categoryValue,
      label: cat?.categoryLabel
    }))
  ];

  const filteredTemplates = templates?.filter(template => {
    const matchesSearch = template?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
                         template?.description?.toLowerCase()?.includes(searchQuery?.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || template?.category === selectedCategory;
    const isNotSystemTemplate = template?.category !== 'system';
    return matchesSearch && matchesCategory && isNotSystemTemplate;
  });

  const formatDate = (date) => {
    const dateObj = new Date(date);
    const now = new Date();
    const diff = Math.floor((now - dateObj) / 86400000);
    if (diff === 0) return 'Today';
    if (diff === 1) return 'Yesterday';
    if (diff < 7) return `${diff} days ago`;
    return dateObj?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'bg-success/10 text-success';
      case 'draft':
        return 'bg-warning/10 text-warning';
      case 'archived':
        return 'bg-muted text-muted-foreground';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Icon name="Loader2" size={48} className="animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading templates...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Icon name="AlertCircle" size={48} className="text-destructive mx-auto mb-4" />
          <p className="text-destructive mb-4">{error}</p>
          <Button onClick={loadData}>Retry</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Category Management Section */}
      <div className="bg-card rounded-xl border border-border p-4 md:p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-primary/10">
              <Icon name="FolderTree" size={24} color="var(--color-primary)" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-foreground">Template Categories</h3>
              <p className="caption text-muted-foreground">Manage template category types</p>
            </div>
          </div>
          <Button variant="default" iconName="Plus" iconPosition="left" onClick={handleCreateCategory}>
            Add Category
          </Button>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {categories?.map((category) => (
            <div
              key={category?.id}
              className="bg-muted rounded-lg p-4 flex items-center justify-between hover:bg-muted/80 transition-colors duration-250"
            >
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-primary/10 flex-shrink-0">
                  <Icon name="Tag" size={16} color="var(--color-primary)" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">{category?.categoryLabel}</p>
                  <p className="caption text-muted-foreground truncate">{category?.categoryValue}</p>
                </div>
                {category?.isSystem && (
                  <span className="caption px-2 py-1 rounded bg-primary/10 text-primary flex-shrink-0">System</span>
                )}
              </div>
              <div className="flex items-center gap-1 ml-2 flex-shrink-0">
                <button
                  onClick={() => handleEditCategory(category)}
                  className="p-2 hover:bg-background rounded-lg transition-colors duration-250"
                  aria-label="Edit category"
                >
                  <Icon name="Edit2" size={16} />
                </button>
                <button
                  onClick={() => handleDeleteCategory(category?.id)}
                  className="p-2 hover:bg-destructive/10 text-destructive rounded-lg transition-colors duration-250"
                  aria-label="Delete category"
                >
                  <Icon name="Trash2" size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Category Dialog */}
      {showCategoryDialog && (
        <>
          <div
            className="fixed inset-0 bg-background/80 backdrop-blur-sm z-[400]"
            onClick={() => setShowCategoryDialog(false)}
            aria-hidden="true"
          />
          <div className="fixed inset-0 flex items-center justify-center z-[401] p-4">
            <div className="bg-card rounded-xl border border-border shadow-2xl w-full max-w-md">
              <div className="flex items-center justify-between px-6 py-4 border-b border-border">
                <h3 className="text-lg font-semibold text-foreground">
                  {editingCategory ? 'Edit Category' : 'Add Category'}
                </h3>
                <button
                  onClick={() => setShowCategoryDialog(false)}
                  className="flex items-center justify-center w-8 h-8 rounded-lg bg-muted hover:bg-muted/80 transition-colors duration-250"
                  aria-label="Close dialog"
                >
                  <Icon name="X" size={18} />
                </button>
              </div>
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Category Label
                  </label>
                  <Input
                    type="text"
                    placeholder="e.g., Quality Control"
                    value={categoryName}
                    onChange={(e) => setCategoryName(e?.target?.value)}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Category Value
                  </label>
                  <Input
                    type="text"
                    placeholder="e.g., quality_control"
                    value={categoryValue}
                    onChange={(e) => setCategoryValue(e?.target?.value?.toLowerCase()?.replace(/\s+/g, '_'))}
                  />
                  <p className="caption text-muted-foreground mt-1">Use lowercase with underscores</p>
                </div>
              </div>
              <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-border">
                <Button variant="outline" onClick={() => setShowCategoryDialog(false)}>
                  Cancel
                </Button>
                <Button
                  variant="default"
                  onClick={handleSaveCategory}
                  disabled={isSavingCategory}
                >
                  {isSavingCategory ? 'Saving...' : editingCategory ? 'Update' : 'Create'}
                </Button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Templates Section */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            type="search"
            placeholder="Search templates..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e?.target?.value)}
          />
          <Select
            options={categoryOptions}
            value={selectedCategory}
            onChange={setSelectedCategory}
            placeholder="Filter by category"
          />
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center bg-muted rounded-xl p-1">
            <button
              className={`p-2 rounded-lg transition-colors duration-250 ${
                viewMode === 'grid' ? 'bg-card shadow-sm' : 'hover:bg-card/50'
              }`}
              onClick={() => setViewMode('grid')}
              aria-label="Grid view"
            >
              <Icon name="Grid3x3" size={18} />
            </button>
            <button
              className={`p-2 rounded-lg transition-colors duration-250 ${
                viewMode === 'list' ? 'bg-card shadow-sm' : 'hover:bg-card/50'
              }`}
              onClick={() => setViewMode('list')}
              aria-label="List view"
            >
              <Icon name="List" size={18} />
            </button>
          </div>
          <Button variant="default" iconName="Plus" iconPosition="left" onClick={handleCreateTemplate}>
            Create Template
          </Button>
        </div>
      </div>
      {viewMode === 'grid' ? (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
          {filteredTemplates?.map((template) => (
            <div
              key={template?.id}
              className="bg-card rounded-lg border border-border p-3 hover:shadow-lg transition-all duration-250"
            >
              <div className="flex items-start justify-between mb-2">
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm font-semibold text-foreground mb-1 line-clamp-2">
                    {template?.name}
                  </h3>
                  <div className="flex items-center gap-1.5">
                    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${getStatusColor(template?.status)}`}>
                      {template?.status}
                    </span>
                    {template?.is_system && (
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-primary/10 text-primary">
                        System
                      </span>
                    )}
                  </div>
                </div>
                <div className="relative flex-shrink-0">
                  <button
                    className="p-1.5 hover:bg-muted rounded-lg transition-colors duration-250"
                    onClick={() => setOpenMenuId(openMenuId === template?.id ? null : template?.id)}
                    aria-label="Template options"
                  >
                    <Icon name="MoreVertical" size={16} />
                  </button>
                  {openMenuId === template?.id && (
                    <>
                      <div
                        className="fixed inset-0 z-[100]"
                        onClick={() => setOpenMenuId(null)}
                        aria-hidden="true"
                      />
                      <div className="absolute right-0 top-full mt-1 w-48 bg-card rounded-lg border border-border shadow-lg z-[101] py-1">
                        <button
                          className="w-full px-4 py-2 text-left text-sm text-foreground hover:bg-muted transition-colors duration-250 flex items-center gap-2"
                          onClick={() => {
                            handleEditTemplate(template);
                            setOpenMenuId(null);
                          }}
                        >
                          <Icon name="Edit2" size={16} />
                          Edit Template
                        </button>
                        <button
                          className={`w-full px-4 py-2 text-left text-sm transition-colors duration-250 flex items-center gap-2 ${
                            template?.is_system
                              ? 'text-muted-foreground cursor-not-allowed opacity-50'
                              : 'text-foreground hover:bg-muted'
                          }`}
                          onClick={() => {
                            if (!template?.is_system) {
                              handleCloneTemplate(template);
                              setOpenMenuId(null);
                            }
                          }}
                          disabled={template?.is_system}
                        >
                          <Icon name="Copy" size={16} />
                          Clone Template
                          {template?.is_system && <span className="ml-auto text-xs">(System)</span>}
                        </button>
                        <div className="border-t border-border my-1" />
                        <button
                          className={`w-full px-4 py-2 text-left text-sm transition-colors duration-250 flex items-center gap-2 ${
                            template?.is_system
                              ? 'text-muted-foreground cursor-not-allowed opacity-50'
                              : 'text-destructive hover:bg-destructive/10'
                          }`}
                          onClick={() => {
                            if (!template?.is_system) {
                              handleDeleteTemplate(template?.id);
                              setOpenMenuId(null);
                            }
                          }}
                          disabled={template?.is_system}
                        >
                          <Icon name="Trash2" size={16} />
                          Delete Template
                          {template?.is_system && <span className="ml-auto text-xs">(System)</span>}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>

              <p className="text-xs text-muted-foreground mb-2 line-clamp-2">
                {template?.description}
              </p>

              <div className="space-y-1 mb-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">Category</span>
                  <span className="text-xs font-medium text-foreground">{template?.category}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">Version</span>
                  <span className="text-xs font-medium text-foreground">v{template?.version}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">Tasks</span>
                  <span className="text-xs font-medium text-foreground">{template?.tasks}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">Duration</span>
                  <span className="text-xs font-medium text-foreground">{template?.estimatedDuration}</span>
                </div>
              </div>

              <div className="flex items-center gap-1.5 mb-2 pb-2 border-b border-border">
                <Icon name="User" size={12} color="var(--color-muted-foreground)" />
                <span className="text-xs text-muted-foreground truncate">{template?.author}</span>
                <span className="text-xs text-muted-foreground">•</span>
                <span className="text-xs text-muted-foreground truncate">{formatDate(template?.lastModified)}</span>
              </div>

              <div className="flex items-center gap-1.5">
                <Button 
                  variant="outline" 
                  size="sm" 
                  fullWidth 
                  iconName="Copy" 
                  onClick={() => handleCloneTemplate(template)}
                  disabled={template?.is_system}
                  className="text-xs py-1 h-7"
                >
                  Clone
                </Button>
                <Button 
                  variant="default" 
                  size="sm" 
                  fullWidth 
                  iconName="Edit2" 
                  onClick={() => handleEditTemplate(template)}
                  className="text-xs py-1 h-7"
                >
                  Edit
                </Button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-card rounded-xl border border-border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted border-b border-border">
                <tr>
                  <th className="px-4 md:px-6 py-3 text-left text-sm font-semibold text-foreground">Template</th>
                  <th className="px-4 md:px-6 py-3 text-left text-sm font-semibold text-foreground hidden md:table-cell">Category</th>
                  <th className="px-4 md:px-6 py-3 text-left text-sm font-semibold text-foreground hidden lg:table-cell">Version</th>
                  <th className="px-4 md:px-6 py-3 text-left text-sm font-semibold text-foreground hidden lg:table-cell">Usage</th>
                  <th className="px-4 md:px-6 py-3 text-left text-sm font-semibold text-foreground">Status</th>
                  <th className="px-4 md:px-6 py-3 text-right text-sm font-semibold text-foreground">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {filteredTemplates?.map((template) => (
                  <tr key={template?.id} className="hover:bg-muted/50 transition-colors duration-250">
                    <td className="px-4 md:px-6 py-4">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-medium text-foreground truncate">{template?.name}</p>
                          {template?.is_system && (
                            <span className="inline-flex items-center px-2 py-1 rounded-lg text-xs font-medium bg-primary/10 text-primary flex-shrink-0">
                              System
                            </span>
                          )}
                        </div>
                        <p className="caption text-muted-foreground truncate">{template?.author} • {formatDate(template?.lastModified)}</p>
                      </div>
                    </td>
                    <td className="px-4 md:px-6 py-4 hidden md:table-cell">
                      <span className="text-sm text-foreground">{template?.category}</span>
                    </td>
                    <td className="px-4 md:px-6 py-4 hidden lg:table-cell">
                      <span className="caption text-muted-foreground">v{template?.version}</span>
                    </td>
                    <td className="px-4 md:px-6 py-4 hidden lg:table-cell">
                      <span className="caption text-muted-foreground">{template?.usageCount} times</span>
                    </td>
                    <td className="px-4 md:px-6 py-4">
                      <span className={`inline-flex items-center px-2 py-1 rounded-lg text-xs font-medium ${getStatusColor(template?.status)}`}>
                        {template?.status}
                      </span>
                    </td>
                    <td className="px-4 md:px-6 py-4">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          className={`p-2 rounded-lg transition-colors duration-250 ${
                            template?.is_system
                              ? 'text-muted-foreground cursor-not-allowed opacity-50'
                              : 'hover:bg-muted'
                          }`}
                          onClick={() => !template?.is_system && handleCloneTemplate(template)}
                          aria-label="Clone template"
                          disabled={template?.is_system}
                        >
                          <Icon name="Copy" size={16} />
                        </button>
                        <button
                          className="p-2 hover:bg-muted rounded-lg transition-colors duration-250"
                          onClick={() => handleEditTemplate(template)}
                          aria-label="Edit template"
                        >
                          <Icon name="Edit2" size={16} />
                        </button>
                        <button
                          className={`p-2 rounded-lg transition-colors duration-250 ${
                            template?.is_system
                              ? 'text-muted-foreground cursor-not-allowed opacity-50'
                              : 'hover:bg-destructive/10 text-destructive'
                          }`}
                          onClick={() => !template?.is_system && handleDeleteTemplate(template?.id)}
                          aria-label="Delete template"
                          disabled={template?.is_system}
                        >
                          <Icon name="Trash2" size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4 px-4 md:px-6 py-4 bg-card rounded-xl border border-border">
        <span className="caption text-muted-foreground">
          Showing {filteredTemplates?.length} of {templates?.length} templates
        </span>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" iconName="ChevronLeft" disabled>
            Previous
          </Button>
          <Button variant="outline" size="sm" iconName="ChevronRight">
            Next
          </Button>
        </div>
      </div>

      {/* Template Dialog */}
      {showTemplateDialog && (
        <>
          <div
            className="fixed inset-0 bg-background/80 backdrop-blur-sm z-[400]"
            onClick={() => setShowTemplateDialog(false)}
            aria-hidden="true"
          />
          <div className="fixed inset-0 flex items-center justify-center z-[401] p-4">
            <div className="bg-card rounded-xl border border-border shadow-2xl w-full max-w-md">
              <div className="flex items-center justify-between px-6 py-4 border-b border-border">
                <h3 className="text-lg font-semibold text-foreground">
                  {editingTemplate ? 'Edit Template' : 'Create Template'}
                </h3>
                <button
                  onClick={() => setShowTemplateDialog(false)}
                  className="flex items-center justify-center w-8 h-8 rounded-lg bg-muted hover:bg-muted/80 transition-colors duration-250"
                  aria-label="Close dialog"
                >
                  <Icon name="X" size={18} />
                </button>
              </div>
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Template Name
                  </label>
                  <Input
                    type="text"
                    placeholder="e.g., Assembly Line Workflow"
                    value={templateName}
                    onChange={(e) => setTemplateName(e?.target?.value)}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Description
                  </label>
                  <textarea
                    className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
                    placeholder="Describe the template purpose..."
                    rows={3}
                    value={templateDescription}
                    onChange={(e) => setTemplateDescription(e?.target?.value)}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Category
                  </label>
                  <Select
                    options={categories?.map(cat => ({
                      value: cat?.categoryValue,
                      label: cat?.categoryLabel
                    }))}
                    value={templateCategory}
                    onChange={setTemplateCategory}
                    placeholder="Select category"
                  />
                </div>
              </div>
              <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-border">
                <Button variant="outline" onClick={() => setShowTemplateDialog(false)}>
                  Cancel
                </Button>
                <Button
                  variant="default"
                  onClick={handleSaveTemplate}
                  disabled={isSavingTemplate}
                >
                  {isSavingTemplate ? 'Saving...' : editingTemplate ? 'Update' : 'Create'}
                </Button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default TemplateManagementPanel;